import classNames from "classnames";
import { useRouter } from "next/router";
import * as React from "react";
import { Button } from "@components/Button";
import type { ShopDetailSchedule_ShopFragment } from "@components/ShopDetail/Schedule/__generated__/fragments";
import TelDialog from "@components/ShopDetail/TelDialog";
import type { UsersShopCalendarDayType } from "@graphql/__generated__/types";
import { ShopReservabilityTypeEnum } from "@graphql/__generated__/types";
import { paths } from "@libs/paths";
import { IFImg } from "@libs/utils/IFImg";
import {
  convertDayjs,
  formats,
  isSaturday,
  isSunday,
  toLocaleStr,
} from "@libs/utils/date";
import { GTMEvent, GTMEvents } from "@libs/utils/gtm";
import styles from "./index.module.css";

type Props = {
  shop: ShopDetailSchedule_ShopFragment;
};

const FOR_ANALYTICS_BUTTON_LABEL = "予約スケジュール";

const ScheduleList: React.FC<Props> = ({ shop }) => {
  const weekList = shop.calendarDays;
  const [isExpanded, setIsExpanded] = React.useState(false);
  const firstWeek = weekList.slice(0, 7);
  const weeks = isExpanded ? 8 : 4;
  const daysPerWeek = 7;
  const limitWeek = weeks * daysPerWeek;
  const visibleDays = weekList.slice(0, limitWeek);
  const [open, setOpen] = React.useState(false);

  const router = useRouter();
  const handleDayClick = (calendarDay: UsersShopCalendarDayType) => {
    if (calendarDay.reservability === ShopReservabilityTypeEnum.Reservable) {
      GTMEvent(
        GTMEvents.toReservationFormPressed(
          FOR_ANALYTICS_BUTTON_LABEL,
          shop.uuid,
          undefined,
          toLocaleStr(calendarDay.date, "ymdHyphen")
        )
      );

      router.push(
        paths.shopRequestReservation(shop.uuid, {
          date: calendarDay.date,
          matchId: calendarDay.defaultReservableMatchId ?? undefined,
        })
      );
    } else if (calendarDay.reservability === ShopReservabilityTypeEnum.Tel) {
      GTMEvent(GTMEvents.telButtonPressed(FOR_ANALYTICS_BUTTON_LABEL, true));
      setOpen(true);
    }
  };

  const iconMap: { [key in ShopReservabilityTypeEnum]: React.ReactElement } = {
    [ShopReservabilityTypeEnum.Reservable]: (
      <div className={classNames(styles.iconMap, styles.able)}>
        <img src="/img/icon_ok_circle.svg" />
      </div>
    ),
    [ShopReservabilityTypeEnum.Tel]: (
      <div className={classNames(styles.iconMap, styles.tel)}>TEL</div>
    ),
    [ShopReservabilityTypeEnum.Full]: (
      <div className={classNames(styles.iconMap, styles.full)}>満席</div>
    ),
    [ShopReservabilityTypeEnum.None]: (
      <div className={classNames(styles.iconMap, styles.unable)}>
        <span />
      </div>
    ),
  };

  return (
    <>
      <ul className={styles.weekday}>
        {firstWeek.map((calendarDay, index) => {
          const day = convertDayjs(calendarDay.date);
          return (
            <li
              className={classNames(
                styles.weekdayItems,
                isSunday(day) ? styles.isSun : isSaturday(day) && styles.isSat
              )}
              key={index}
            >
              {day.format(formats.weekOnly)}
            </li>
          );
        })}
      </ul>

      <div className={styles.line}>
        {visibleDays.map((dayObj) => {
          const day = convertDayjs(dayObj.date);
          return (
            <div
              className={classNames(styles.lineItems, {
                [styles.noLink]:
                  dayObj.reservability === ShopReservabilityTypeEnum.Full ||
                  dayObj.reservability === ShopReservabilityTypeEnum.None,
              })}
              key={dayObj.date}
              onClick={() => {
                handleDayClick(dayObj);
              }}
            >
              <div
                className={classNames(
                  styles.day,
                  isSunday(day) || dayObj.isHoliday
                    ? styles.isSun
                    : isSaturday(day) && styles.isSat
                )}
              >
                {toLocaleStr(day, "dateOnlyEn")}
              </div>
              <div
                className={classNames(styles.scheduled, {
                  [styles.hasMatch]: dayObj.hasMatch,
                })}
              >
                <span>{dayObj.hasMatch ? "上映" : "--"}</span>
              </div>
              {iconMap[dayObj.reservability]}
            </div>
          );
        })}
      </div>
      <Button
        className={styles.expandButton}
        styleType="outlined"
        colorType="secondary"
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <div className={styles.expandButtonLabel}>
          <img
            className={classNames(styles.expandButtonIcon, {
              [styles.isExpanded]: isExpanded,
            })}
            src={IFImg("/img/icon/arrow_under.svg")}
            alt=""
          />
          {isExpanded ? "閉じる" : "次の4週間を表示"}
        </div>
      </Button>
      <TelDialog
        fromLabel={FOR_ANALYTICS_BUTTON_LABEL}
        uuid={shop.uuid}
        name={shop.name}
        tel={shop.tel}
        telWithHyphen={shop.telWithHyphen}
        canReserve={false} // TELボタンが表示されるケースは常に予約不可のため、falseを設定
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
};

export default ScheduleList;
